import Phaser from 'phaser';
import { TextureKey } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import type { WorldScale } from '../core/WorldScale.js';

const B = TUNING.bonus;
const C = TUNING.coins;

interface Flying {
  img: Phaser.GameObjects.Image;
  /** subsidy it brings (€) */
  eur: number;
  checkpoint?: number;
  /** current speed (WU/s) */
  speed: number;
}

/**
 * Bonus coins = Grundzulage on their way to the figure: taken from the course (CoinField.pullBonus) and
 * pulled towards the figure (magnetic, accelerating) – the player does not have to do anything, like the
 * subsidy is paid automatically. `onArrive` credits it.
 */
export class BonusField {
  private readonly pool: Phaser.GameObjects.Image[] = [];
  private readonly flying: Flying[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly ws: WorldScale,
    private readonly depth: number,
    private readonly onArrive: (x: number, y: number, eur: number, checkpoint?: number) => void,
  ) {}

  /** subsidy (€) still on its way */
  get pendingEur(): number {
    return this.flying.reduce((sum, f) => sum + f.eur, 0);
  }

  /** A bonus coin at (x, y) bringing `eur` starts flying to the figure – first with the speed of the course. */
  attract(x: number, y: number, eur: number, checkpoint?: number): void {
    const size = this.ws.px(C.displaySize) * B.scale;
    const img = this.obtain().setPosition(x, y).setDisplaySize(size, size);
    this.flying.push({ img, eur, checkpoint, speed: TUNING.world.scrollSpeed });
  }

  /** Move the coins towards the target (pixels); the ones that reach it are credited. */
  update(dtSec: number, tx: number, ty: number, reach: number): void {
    for (let i = this.flying.length - 1; i >= 0; i--) {
      const f = this.flying[i];
      f.speed = Math.min(B.maxSpeed, f.speed + B.accel * dtSec);
      const dx = tx - f.img.x;
      const dy = ty - f.img.y;
      const dist = Math.hypot(dx, dy);
      const step = this.ws.px(f.speed * dtSec);
      if (dist <= Math.max(reach, step)) {
        this.flying.splice(i, 1);
        this.free(f.img);
        this.onArrive(tx, ty, f.eur, f.checkpoint);
        continue;
      }
      f.img.x += (dx / dist) * step;
      f.img.y += (dy / dist) * step;
      f.img.angle += 360 * dtSec;
    }
  }

  /** Credit everything still on its way at once (end of the run). */
  arriveAll(tx: number, ty: number): void {
    while (this.flying.length) {
      const f = this.flying.pop()!;
      this.free(f.img);
      this.onArrive(tx, ty, f.eur, f.checkpoint);
    }
  }

  /** Drop everything without crediting (new run). */
  reset(): void {
    for (const f of this.flying) this.free(f.img);
    this.flying.length = 0;
  }

  private obtain(): Phaser.GameObjects.Image {
    let img = this.pool.find((q) => !q.active);
    if (!img) {
      img = this.scene.add.image(0, 0, TextureKey.CoinBonus).setDepth(this.depth);
      this.pool.push(img);
    }
    this.scene.tweens.killTweensOf(img);
    return img.setActive(true).setVisible(true).setAngle(0);
  }

  private free(img: Phaser.GameObjects.Image): void {
    this.scene.tweens.killTweensOf(img);
    img.setActive(false).setVisible(false);
  }
}
